import { RestAdminDuplicateFeed } from '../../schema-copy/api-schema';
import { registrableDomain } from '../../schema-copy/site-css';

/** Statuses a duplicate may be merged away in (the sync-server's MERGEABLE_STATUSES). */
const MERGEABLE_STATUSES = ['ACTIVE', 'BAD', 'UNUSED'];

export const feedName = (feed: RestAdminDuplicateFeed) => feed.shortTitle || feed.title || feed.id;

/**
 * The feed of a group to keep by default: one in the catalog first (a catalog entry is curated
 * work, and such a feed can't be merged away), then the one with the most subscribers, with
 * referenced archives, ACTIVE, verified, the most recently posting, and finally the oldest.
 */
export function defaultKeeper(feeds: RestAdminDuplicateFeed[]): RestAdminDuplicateFeed {
  const score = (f: RestAdminDuplicateFeed) =>
    [+f.hasCatalog, f.subscriberCount, +(f.referencedArchives > 0), +(f.feedStatus == 'ACTIVE'), +f.manuallyVerified, f.lastNewPostPubDate ?? '', -Date.parse(f.createdAt)] as const;
  return feeds.reduce((best, f) => {
    const a = score(f), b = score(best);
    for (let i = 0; i < a.length; i++)
      if (a[i] != b[i]) return a[i] > b[i] ? f : best;
    return best;
  });
}

/** The feed's host (its URL as shown in the list, else siteUrl) without a leading www. or blog., lowercased; undefined
 *  when neither URL has a host. */
export function feedDomain(feed: { siteUrl?: string; url: string }): string | undefined {
  for (const url of [feed.url, feed.siteUrl]) {
    const host = url?.trim().match(/^(?:[a-z][a-z0-9+.-]*:\/\/)?([^/?#:@\s]+)/i)?.[1];
    if (host?.includes('.')) return host.toLowerCase().replace(/^(www|blog)\./, '');
  }
  return undefined;
}

/** The name with the feed's domain appended (`Name (example.com)`); undefined when the name
 *  already ends with `)` or the feed has no domain. An empty name takes the feed's title. */
export function nameWithDomain(feed: RestAdminDuplicateFeed, name = feedName(feed)): string | undefined {
  const domain = feedDomain(feed);
  const base = name.trim() || feed.title;
  return domain && !base.endsWith(')') ? `${base} (${domain})` : undefined;
}

/** Ids of the feeds whose shown name (feedName, ignoring case) another feed of the group has too. */
export function sharedNames(feeds: RestAdminDuplicateFeed[]): Set<string> {
  const key = (f: RestAdminDuplicateFeed) => feedName(f).trim().toLowerCase();
  const counts = new Map<string, number>();
  feeds.forEach(f => counts.set(key(f), (counts.get(key(f)) ?? 0) + 1));
  return new Set(feeds.filter(f => counts.get(key(f))! > 1).map(f => f.id));
}

/** True when every feed is on another site (registrable domain: foo.com and bar.com, but not
 *  foo.com and blog.foo.com): different feeds, so they get told apart by their domains, not merged. */
export function allDifferentSites(feeds: RestAdminDuplicateFeed[]): boolean {
  const sites = feeds.map(f => feedDomain(f));
  return sites.every(Boolean) && new Set(sites.map(d => registrableDomain(d!))).size == feeds.length;
}

/** Why a feed can't be merged into another one (undefined when it can). Mirrors the server's checks. */
export function mergeBlocker(feed: RestAdminDuplicateFeed): string | undefined {
  if (!MERGEABLE_STATUSES.includes(feed.feedStatus)) return `${feed.feedStatus} feeds stay`;
  if (feed.subscriberCount) return `${feed.subscriberCount} subscriber${feed.subscriberCount == 1 ? '' : 's'}`;
  if (feed.hasCatalog) return 'in the catalog';
  if (feed.referencedArchives) return 'has starred/read-later posts';
  return undefined;
}

/**
 * A feed URL reduced to what tells feeds apart: no scheme (http and https are the same), a
 * lowercased host, no trailing slash, and no trailing /atom (WordPress serves the same posts at
 * /feed/ and /feed/atom/).
 */
export function feedUrlKey(url: string): string {
  return url.trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^[^/?#]+/, host => host.toLowerCase())
    .replace(/\/+$/, '')
    .replace(/\/atom$/i, '');
}

/**
 * Feeds of a group that are most likely the very same feed (another format or an old URL): either
 * the same name, post count and last post (feeds without a last post never match that way), or
 * URLs that only differ as feedUrlKey ignores. Matches chain: a feed matching one of a set by
 * content and another by URL joins both. Returns a set number (1, 2, …) per such feed, so several
 * sets in one group can be told apart.
 */
export function likelySameSets(feeds: RestAdminDuplicateFeed[]): Map<string, number> {
  // Union-find over the feeds: each matching key joins the feeds sharing it.
  const parent = feeds.map((_, i) => i);
  const root = (i: number): number => parent[i] == i ? i : (parent[i] = root(parent[i]));
  const firstWithKey = new Map<string, number>();
  const join = (key: string, i: number) => {
    const first = firstWithKey.get(key);
    if (first == undefined) firstWithKey.set(key, i);
    else parent[root(i)] = root(first);
  };
  feeds.forEach((f, i) => {
    if (f.lastNewPostPubDate) join(`content|${f.title.trim().toLowerCase()}|${f.postCount}|${f.lastNewPostPubDate}`, i);
    join(`url|${feedUrlKey(f.url)}`, i);
  });

  const members = new Map<number, string[]>();
  feeds.forEach((f, i) => members.set(root(i), [...(members.get(root(i)) ?? []), f.id]));
  const result = new Map<string, number>();
  let set = 0;
  members.forEach(ids => {
    if (ids.length < 2) return;
    set++;
    ids.forEach(id => result.set(id, set));
  });
  return result;
}

/**
 * Feeds whose shown name (feedName) another feed of the group has too, while the two are not in
 * the same likelySameSets set: different feeds that readers can't tell apart, to be renamed.
 */
export function confusableNames(feeds: RestAdminDuplicateFeed[], sameSets: Map<string, number>): Set<string> {
  const result = new Set<string>();
  feeds.forEach(a => feeds.forEach(b => {
    if (a.id != b.id && feedName(a).trim().toLowerCase() == feedName(b).trim().toLowerCase()
        && (sameSets.get(a.id) == undefined || sameSets.get(a.id) != sameSets.get(b.id)))
      result.add(a.id);
  }));
  return result;
}
